/**
 * Shared permission helpers for Process Compliance pages.
 *
 * Follows the same selectedRole pattern used across the app (e.g. utilization
 * charts) by reusing isEmployeeAccessRole from accessRole.ts for case-insensitive
 * employee-tier detection.
 */
import { isEmployeeAccessRole } from "./accessRole";

/**
 * Returns true when the active role grants unrestricted compliance access.
 * Case-insensitive — mirrors the lowercase comparison used by isEmployeeAccessRole.
 */
export function isComplianceAdmin(selectedRole: string | undefined | null): boolean {
  const role = (selectedRole ?? "").toLowerCase();
  return role === "admin" || role === "hr";
}

/**
 * Returns true when the current user may edit (fill responses / comments on) a
 * compliance assignment.
 *
 * Rules (case-insensitive role matching throughout):
 *  - Completed assignments are never editable.
 *  - Admin / HR can edit any non-completed assignment.
 *  - Employee can edit only a DIRECT assignment where they are the assigned_to.
 *  - Process Compliance Officer can edit only a PROJECT_BASED assignment where
 *    they are the assigned_to.
 *  - Manager and all other roles are read-only.
 *
 * assignmentType is optional; when omitted the type-role check is skipped so
 * the function degrades gracefully to a pure identity check.
 */
export function canEditAssignment(params: {
  selectedRole: string | undefined | null;
  personId: string | undefined | null;
  assignedTo: string;
  status: string;
  assignmentType?: string;
}): boolean {
  const { selectedRole, personId, assignedTo, status, assignmentType } = params;
  const roleLower = (selectedRole ?? "").toLowerCase();

  if (status === "completed") return false;

  // Admin / HR — unrestricted edit (case-insensitive)
  if (roleLower === "admin" || roleLower === "hr") return true;

  // Employee tier (Employee + Process Compliance Officer) — own assignments only
  if (isEmployeeAccessRole(selectedRole)) {
    const isAssigned = Boolean(personId) && personId === assignedTo;
    if (!isAssigned) return false;

    // Enforce assignment-type / role alignment when the type is known
    if (assignmentType === "project_based" && roleLower === "employee") return false;
    if (assignmentType === "direct" && roleLower === "process compliance officer") return false;

    return true;
  }

  // Manager and any other role → read-only
  return false;
}

/**
 * Returns true when the user is viewing an assignment they did NOT fill —
 * i.e. a linked team member reading a submitted project-based compliance report.
 */
export function isLinkedTeamViewer(params: {
  selectedRole: string | undefined | null;
  personId: string | undefined | null;
  assignedTo: string;
}): boolean {
  const { selectedRole, personId, assignedTo } = params;
  if (isComplianceAdmin(selectedRole)) return false;
  return Boolean(personId) && personId !== assignedTo;
}
